import { ApiError, errorText, get, post, put } from '@/api/client';
import { useTenantWrite, useUsers } from '@/api/data';
import { useLabour } from '@/api/labour';
import {
  type ImportFields,
  type ImportScope,
  type ImportSource,
  type ScopeDepartment,
  type SetupDraft,
  useImportScope,
  useImportScopeWrite,
  useOnboarding,
  useSetupDraftWrite,
  useSetupDrafts,
} from '@/api/onboarding';
import { usePayrollTypes } from '@/api/payroll';
import { useShiftTypes } from '@/api/shifts';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import type { Billing } from '@/pages/Abonnement';
import { MS_DENIED, MS_ERRORS, MS_OFF, startMicrosoft, useMicrosoftOn } from '@/pages/ansatt/Importer';
import { KEY, type PowerOfficeStatus, usePowerOffice } from '@/pages/lonn/PowerOffice';
import {
  BASIS,
  DEPT_NEEDED,
  type Missing,
  type PayTypeSuggestion,
  type PowerOfficeMatch,
  addFromGo,
} from '@/pages/lonn/PowerOfficeMatch';
import { KEY as TX_KEY, TripletexKeySteps, type TripletexStatus, useTripletex } from '@/pages/lonn/Tripletex';
import { POWEROFFICE, type PayrollSystem, TRIPLETEX, chosen, systemOf } from '@/pages/lonn/payrollSystem';
import { PocChatPanel, draftForHugin } from '@/poc-chat/Panel';
import { UndoList } from '@/poc-chat/SettingEdit';
import { SHOW_PROPOSALS, useDock } from '@/poc-chat/attach';
import { typeTime } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { Status } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import css from './oppsett.module.css';

type Step = 'settings' | 'company' | 'employees' | 'rules' | 'hugin' | 'review';
/**
 * Three stages, the server's six steps under them (#154 HUG-01, owner 2026-11-04: «first step is to choose
 * the platform one uses, then we try importing and see what we have, the ai steps later»). The company,
 * the source departments, what was found, the people and the pay types are parts of stage 2; Hugin and the
 * trial are stage 3. `setup_step` keeps where the administrator is, so a reload or another day resumes there.
 */
const STAGES = [
  { label: 'Velg system', step: 'settings' as Step },
  { label: 'Se over og importer', step: 'company' as Step },
  { label: 'Fullfør med AI', step: 'hugin' as Step },
];
const stageOf = (s: Step) => (s === 'settings' ? 0 : s === 'hugin' || s === 'review' ? 2 : 1);

/** What the setup can start from, and what each really gives today (#296 owns the provider contracts). */
const SOURCES: { id: ImportSource; name: string; sub: string; icon: IconName; gives: string }[] = [
  {
    id: 'poweroffice',
    name: 'PowerOffice Go',
    sub: 'Ansatte, avdelinger og lønnsarter fra lønnssystemet',
    icon: 'banknote',
    gives:
      'Ansatte med ansattnummer, lønn og kontrakt, avdelingene de hører til, og lønnsartene. Du velger først hvilke avdelinger som hører til bedriften. Ingenting skrives til PowerOffice under oppsettet.',
  },
  {
    id: 'tripletex',
    name: 'Tripletex',
    sub: 'Ansatte, avdelinger og koden for vanlige timer',
    icon: 'banknote',
    gives:
      'Ansatte med ansattnummer, lønn og kontrakt, avdelingene de hører til, og koden for vanlige timer. Tillegg settes under Innstillinger, Lønn. Ingenting skrives til Tripletex under oppsettet.',
  },
  {
    id: 'visma',
    name: 'Visma',
    sub: 'Ingen direkte kobling. Visma-fil støttes ikke ennå: Timer til lønn lager Tripletex-filen',
    icon: 'document',
    gives:
      'Visma har ingen direkte kobling i Timefly, så ingenting hentes derfra. Legg inn de ansatte fra Excel eller selv. Visma-fil støttes ikke ennå: Timer til lønn lager Tripletex-filen.',
  },
  {
    id: 'microsoft',
    name: 'Microsoft 365',
    sub: 'Navn og e-post fra bedriftens brukerkatalog',
    icon: 'users',
    gives:
      'Navn, e-post og telefon for de aktive brukerne i katalogen, etter at en administrator i Microsoft 365 har godkjent tilgangen. Fødselsdato fylles inn per person. Lønn og lønnsarter kommer ikke derfra.',
  },
  {
    id: 'fil',
    name: 'Excel eller CSV',
    sub: 'Ansattlisten dere har i et regneark',
    icon: 'upload',
    gives: 'Kolonnene i filen leses og sjekkes før noen legges til. Du ser hver person før du velger.',
  },
  {
    id: 'manuell',
    name: 'Legg inn selv',
    sub: 'Begynn med én ansatt, legg til flere når det passer',
    icon: 'userPlus',
    gives: 'Ny ansatt, én om gangen. Ingen får e-post før du selv sender velkomsten.',
  },
];
const PAYROLL = [
  { id: 'poweroffice', name: 'PowerOffice Go' },
  { id: 'tripletex', name: 'Tripletex' },
  { id: 'visma', name: 'Visma' },
  { id: 'none', name: 'Annet, eller vet ikke' },
] as const;
type PayChoice = (typeof PAYROLL)[number]['id'];
const nameOf = (k: ImportSource | null | undefined) => SOURCES.find((x) => x.id === k)?.name ?? '';
const isPayroll = (k: ImportSource | null | undefined): k is 'poweroffice' | 'tripletex' | 'visma' =>
  k === 'poweroffice' || k === 'tripletex' || k === 'visma';
/** the source on record; a company that started before #154 has its payroll system as its source */
const sourceOf = (t: Parameters<typeof systemOf>[0], d: SetupDraft | undefined): ImportSource | null =>
  (d?.fields as ImportFields | null)?.kilde ?? systemOf(t) ?? null;

const osloDay = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date(iso));
const kr = (n: number) => `${n.toLocaleString('nb-NO')} kr`;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
// the site's booking form: help with the setup is offered, never forced (plan 0028)
const HELP = 'https://timefly.no/#demo';

type Write = Parameters<ReturnType<typeof useTenantWrite>['mutateAsync']>[0]['body'];
interface Setup {
  go: (body: Write, done?: boolean) => Promise<boolean>;
  busy: boolean;
  /** the payroll system whose link the steps read (#183, #187); none for Visma, «Annet» and unchosen */
  sys: PayrollSystem | null;
  /** where the people come from (#154 HUG-01) */
  kilde: ImportSource | null;
  draft: SetupDraft | undefined;
  /** the last saved outcome in this stage, said once (OP-07); leaving the stage drops it */
  receipt: string | null;
  setReceipt: (r: string | null) => void;
  done: boolean[];
}
const Ctx = createContext<Setup | null>(null);
const useSetup = () => useContext(Ctx) as Setup;

/**
 * The first-run setup of a new company (plan 0028 slice 3; the wizard of 2026-10-19; platform first since
 * #154 operational23-2): 1 the system the company uses today, 2 what can be taken from it, reviewed before
 * anything is saved, 3 Hugin for what is still missing, beside the setup itself. Outside the app's shell.
 * Every stage can be opened from the indicator, the import can be put off with a date (not done), and
 * «Hopp over oppsettet» ends it. Nothing is written before a button says what it writes, nothing is
 * written to the payroll system, and nobody gets mail.
 */
export default function Oppsett() {
  const { tenant } = useTenant();
  const nav = useNavigate();
  const tw = useTenantWrite();
  const admin = !!tenant?.setup_step && !!tenant?.me?.is_admin;
  const drafts = useSetupDrafts(admin);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<string | null>(null);
  const stepNow = tenant?.setup_step as Step | null | undefined;
  const stageNow = stepNow ? stageOf(stepNow) : -1;
  // a new step starts at the top: the card is taller than the window on a laptop
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the step changes, reads nothing of it
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepNow]);
  // a receipt is about the stage it was made in: back at the source review, Hugin's is gone (OP-07)
  // biome-ignore lint/correctness/useExhaustiveDependencies: the stage is the trigger
  useEffect(() => setReceipt(null), [stageNow]);
  if (!tenant) return null;
  const step = tenant.setup_step as Step | null | undefined;
  // the setup is the administrator's; anyone else, or a company past it, goes home
  if (!step || !tenant.me?.is_admin) return <Navigate to="/" replace />;
  const draft = drafts.data?.find((d) => d.setup === 'import');
  const kilde = sourceOf(tenant, draft);

  async function go(body: Write, done?: boolean) {
    if (!tenant) return false;
    setBusy(true);
    try {
      await tw.mutateAsync({ id: tenant.id, body });
      if (done) nav('/', { replace: true });
      return true;
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
      return false;
    } finally {
      setBusy(false);
    }
  }
  const sys =
    tenant.payroll_system === 'poweroffice'
      ? POWEROFFICE
      : tenant.payroll_system === 'tripletex'
        ? TRIPLETEX
        : null;
  // stage 1 is done once a source and the payroll question are answered (owner 2026-11-04: «required to
  // choose»); stage 2 once the import was reviewed and the administrator went on, never by a deferral;
  // stage 3 by finishing the setup
  const done = [chosen(tenant) && !!kilde, draft?.status === 'done', false];
  const value: Setup = { go, busy, sys, kilde, draft, receipt, setReceipt, done };
  const stage = stageOf(step);

  return (
    <Ctx.Provider value={value}>
      <div className={css.page}>
        <header className={css.head}>
          <div className={css.brand}>
            <b>timefly</b>
            <span>{T('Oppsett')}</span>
          </div>
          <button
            type="button"
            className={css.skip}
            disabled={busy}
            onClick={() => go({ setup_step: null }, true)}
          >
            {T('Hopp over oppsettet')}
          </button>
        </header>
        <main className={css.main} data-stage={stage + 1}>
          {stage === 0 && <VelgSystem />}
          {stage === 1 && (step === 'employees' ? <People /> : step === 'rules' ? <Rules /> : <Company />)}
          {step === 'hugin' && <FullforAI />}
          {step === 'review' && <Review />}
        </main>
      </div>
    </Ctx.Provider>
  );
}

/** The title, its line, an action on the right, then the one three-stage indicator (HUG-01). */
function Frame({
  title,
  sub,
  aside,
  children,
}: { title: ReactNode; sub?: ReactNode; aside?: ReactNode; children: ReactNode }) {
  const { tenant } = useTenant();
  const { go, busy, done } = useSetup();
  const stage = stageOf(tenant?.setup_step as Step);
  return (
    <>
      <div className={css.top}>
        <div>
          <h1 id="oppsett-title">{title}</h1>
          {sub && <p className={css.lead}>{sub}</p>}
        </div>
        {aside && <div className={css.topAside}>{aside}</div>}
      </div>
      <ol className={css.stages} aria-label={T('Steg')}>
        {STAGES.map((s, i) => (
          <li key={s.step}>
            {/* any stage, in any order, before finishing (owner, 2026-09-27) */}
            <button
              type="button"
              className={done[i] ? css.done : undefined}
              aria-current={i === stage ? 'step' : undefined}
              aria-label={`${i + 1}. ${T(s.label)}${done[i] ? `, ${T('ferdig')}` : ''}`}
              disabled={busy || i === stage}
              onClick={() => go({ setup_step: s.step })}
            >
              <span className={css.num} aria-hidden="true">
                {done[i] ? '✓' : i + 1}
              </span>
              <span className={css.stageName}>{T(s.label)}</span>
            </button>
          </li>
        ))}
      </ol>
      <section className={css.work} aria-labelledby="oppsett-title">
        {children}
      </section>
    </>
  );
}

/* ── stage 1: the system the company uses ─────────────────────────────────── */

function VelgSystem() {
  const { tenant } = useTenant();
  const { go, busy, kilde, draft } = useSetup();
  const write = useSetupDraftWrite();
  const first = useAuth((a) => a.user?.name)?.split(' ')[0];
  // nothing pressed for a new company (owner 2026-11-04: not Tripletex by default); a returning one sees its own
  const [src, setSrc] = useState<ImportSource | null>(kilde);
  const stored = systemOf(tenant);
  const [pay, setPay] = useState<PayChoice | null>(chosen(tenant) ? (stored ?? 'none') : null);
  const payroll: PayChoice | null = isPayroll(src) ? src : pay;
  const ready = !!src && !!payroll;
  const picked = SOURCES.find((x) => x.id === src);
  // the same answer stage 2 and the importer read: off on this server is said on the card itself
  const ms = useMicrosoftOn();
  const msOff = !ms.isPending && ms.data?.available !== true;
  async function next() {
    if (!src || !payroll) return;
    if ((draft?.fields as ImportFields | null)?.kilde !== src) {
      try {
        await write.mutateAsync({ setup: 'import', version: draft?.version ?? 0, fields: { kilde: src } });
      } catch (e) {
        toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
        return;
      }
    }
    await go({ payroll_system: payroll === 'none' ? null : payroll, setup_step: 'company' });
  }
  return (
    <Frame
      title={T('Hvilket system bruker dere i dag?')}
      sub={
        <>
          {first ? `${T('Velkommen, {name}', { name: first })}. ` : ''}
          {T('Ta med det dere allerede har. Etter importen hjelper Hugin med det som mangler.')}
        </>
      }
      aside={
        <Link className={css.textLink} to="/kom-i-gang">
          {T('Se alle steg')}
        </Link>
      }
    >
      <div className={css.choose}>
        <fieldset className={css.sources}>
          <legend className="sr-only">{T('Velg systemet dere bruker')}</legend>
          {SOURCES.map((x) => (
            <label key={x.id} className={css.source} data-on={src === x.id || undefined}>
              <span className={css.sourceIcon} aria-hidden="true">
                <Icon name={x.icon} size={22} />
              </span>
              <span className={css.sourceText}>
                <strong>{x.name}</strong>
                <span>{T(x.sub)}</span>
                {x.id === 'microsoft' && msOff && (
                  <span data-testid="oppsett-microsoft-off">
                    <Status tone="neu" icon="lock">
                      {T('Ikke tilgjengelig')}
                    </Status>
                  </span>
                )}
              </span>
              <input
                type="radio"
                name="oppsett-kilde"
                value={x.id}
                checked={src === x.id}
                onChange={() => setSrc(x.id)}
              />
            </label>
          ))}
        </fieldset>
        <aside className={css.gives} aria-live="polite">
          <div className={css.givesBox}>
            <h2>{T('Dette kan dere ta med')}</h2>
            {picked ? (
              <>
                <strong>{picked.name}</strong>
                <p>{T(picked.id === 'microsoft' && msOff ? MS_OFF : picked.gives)}</p>
              </>
            ) : (
              <p>{T('Velg systemet dere bruker, så ser du hva som kan hentes derfra.')}</p>
            )}
          </div>
          {src && !isPayroll(src) && (
            // the payroll question stays required (owner 2026-11-04), asked here when the people come from elsewhere
            <fieldset className={css.payroll}>
              <legend>{T('Hvilket lønnssystem kjører dere lønn i?')}</legend>
              {PAYROLL.map((p) => (
                <label key={p.id}>
                  <input
                    type="radio"
                    name="oppsett-lonn"
                    value={p.id}
                    checked={pay === p.id}
                    onChange={() => setPay(p.id)}
                  />
                  {T(p.name)}
                </label>
              ))}
            </fieldset>
          )}
          <div className={css.choiceFoot}>
            <span>{picked?.name ?? ''}</span>
            <Button tone="pri" disabled={busy || write.isPending || !ready} onClick={next}>
              {T('Fortsett')}
              <Icon name="arrowRight" size={16} />
            </Button>
          </div>
          <p className={css.muted} data-testid="oppsett-start-note">
            {!src
              ? T('Velg systemet først. Ingenting kobles til før du selv gjør det.')
              : !payroll
                ? T('Velg lønnssystemet. Vet du ikke, velg «Annet, eller vet ikke».')
                : T('Du ser alt før noe lagres, og kan hoppe over hvert steg.')}
          </p>
        </aside>
      </div>
    </Frame>
  );
}

/* ── stage 2: the company, the source departments, what was found ─────────── */

/** The link to the payroll system, whichever it is: whether it can be made, whether it is, and to what. */
function useLink(sys: PayrollSystem | null) {
  const p = usePowerOffice(sys?.key === 'poweroffice');
  const t = useTripletex(sys?.key === 'tripletex');
  const d = sys?.key === 'poweroffice' ? p.data : sys?.key === 'tripletex' ? t.data : undefined;
  if (!d) return undefined;
  return {
    available: d.available,
    connected: d.connected,
    name: 'client_name' in d ? d.client_name : (d as TripletexStatus).company_name,
  };
}
const useMatch = (sys: PayrollSystem | null, on: boolean) =>
  useQuery({
    queryKey: [sys?.key, 'match'],
    queryFn: () => get<PowerOfficeMatch>(`${sys?.base}/match`),
    enabled: on && !!sys,
    staleTime: 0,
  });
/** the source system whose people are fetched in stage 2: the payroll system, when it is also the source */
function usePeopleSource() {
  const { sys, kilde } = useSetup();
  return sys && sys.key === kilde ? sys : null;
}
/** a department's name as the screen says it; id 0 is the people on none */
const deptName = (d: Pick<ScopeDepartment, 'id' | 'name' | 'code'> | undefined) =>
  !d ? '' : d.id === 0 ? T('Uten avdeling') : d.name || T('Avdeling {code}', { code: d.code || d.id });

type View = 'bedrift' | 'avdelinger' | 'oversikt';
/** the departments are worth showing: there are some, or a note says why none can be chosen */
const scopeOpen = (s: ImportScope | undefined) => s?.state === 'ready' || s?.state === 'department_needed';
function Company() {
  const { tenant } = useTenant();
  const src = usePeopleSource();
  const link = useLink(src);
  const connected = !!link?.connected;
  const scope = useImportScope(!!src && connected);
  const [view, setView] = useState<View | null>(null);
  // where a return lands: the company first, then the departments once, then what was found
  const auto: View | null = !tenant?.org_number
    ? 'bedrift'
    : src && !link
      ? null
      : src && !connected
        ? 'bedrift'
        : src && scope.isPending
          ? null
          : src && scopeOpen(scope.data) && scope.data?.selected == null
            ? 'avdelinger'
            : 'oversikt';
  const now = view ?? auto;
  // the company step stays on screen while the link is made there; only Continue moves on
  useEffect(() => {
    if (view === null && auto === 'bedrift') setView('bedrift');
  }, [auto, view]);
  if (!now)
    return (
      <Frame title={T('Se over og importer')}>
        <output className={css.muted}>{T('Henter …')}</output>
      </Frame>
    );
  if (now === 'bedrift')
    return (
      <Bedrift
        onNext={() => setView(src && connected && scopeOpen(scope.data) ? 'avdelinger' : 'oversikt')}
      />
    );
  if (now === 'avdelinger' && scope.data)
    return <Scope scope={scope.data} onBack={() => setView('bedrift')} onDone={() => setView('oversikt')} />;
  return <Oversikt scope={scope.data} onView={setView} />;
}

/** The company by its org number (the Enhetsregisteret, read on save), then the payroll system's link. */
function Bedrift({ onNext }: { onNext: () => void }) {
  const { tenant } = useTenant();
  const { go, busy, kilde } = useSetup();
  const src = usePeopleSource();
  const tw = useTenantWrite();
  const [org, setOrg] = useState(tenant?.org_number ?? '');
  const [err, setErr] = useState<string | null>(null);
  const digits = org.replace(/\s/g, '');
  const status = usePowerOffice(src?.key === 'poweroffice');
  const link = useLink(src);
  async function find() {
    if (!tenant) return;
    setErr(null);
    try {
      await tw.mutateAsync({ id: tenant.id, body: { org_number: digits } });
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <Frame
      title={T('Hvilken bedrift?')}
      sub={T('Det dere henter, hører til denne bedriften i Timefly.')}
      aside={<ChangeSystem />}
    >
      <div className={css.panel}>
        <form
          className={css.field}
          onSubmit={(e) => {
            e.preventDefault();
            find();
          }}
        >
          <label htmlFor="oppsett-org">{T('Organisasjonsnummer')}</label>
          <div className={css.row}>
            <input
              id="oppsett-org"
              className={css.input}
              inputMode="numeric"
              autoComplete="off"
              value={org}
              aria-invalid={err ? true : undefined}
              aria-describedby="oppsett-org-hint"
              onChange={(e) => setOrg(e.target.value)}
            />
            <Button type="submit" disabled={tw.isPending || !/^\d{9}$/.test(digits)}>
              {tw.isPending ? T('Søker…') : T('Finn')}
            </Button>
          </div>
          <span id="oppsett-org-hint" className={err ? css.error : css.muted}>
            {err ?? T('9 siffer. Vi slår opp i Enhetsregisteret.')}
          </span>
        </form>
        {tenant?.org_number && (
          <div className={css.found} data-testid="setup-found">
            <div className={css.foundHead}>
              <strong>{tenant.brreg_name ?? tenant.name}</strong>
              {tenant.brreg_name ? (
                <Status tone="ok">{T('Funnet i Enhetsregisteret')}</Status>
              ) : (
                <Status tone="neu">{T('Enhetsregisteret svarte ikke, nummeret er lagret')}</Status>
              )}
            </div>
            <div className={css.facts}>
              <div>
                <small>{T('Organisasjonsnummer')}</small>
                {tenant.org_number}
              </div>
              {tenant.nace_text && (
                <div>
                  <small>{T('Bransje')}</small>
                  {tenant.nace_text}
                </div>
              )}
            </div>
          </div>
        )}
        {src?.key === 'poweroffice' && <PowerOfficeBox status={status.data} />}
        {src?.key === 'tripletex' && link?.available && (
          <TripletexBox name={link.connected ? link.name : null} />
        )}
        <div className={css.foot}>
          <Button tone="pri" disabled={busy} onClick={onNext}>
            {src && link?.connected ? T('Hent ansatte fra {system}', { system: src.short }) : T('Fortsett')}
            <Icon name="arrowRight" size={16} />
          </Button>
          <button
            type="button"
            className={css.ghost}
            disabled={busy}
            onClick={() => go({ setup_step: 'settings' })}
          >
            {T('Tilbake til systemvalg')}
          </button>
          {kilde && !src && <span className={css.muted}>{nameOf(kilde)}</span>}
        </div>
      </div>
    </Frame>
  );
}
function ChangeSystem() {
  const { go, busy } = useSetup();
  return (
    <button
      type="button"
      className={css.textLink}
      disabled={busy}
      onClick={() => go({ setup_step: 'settings' })}
    >
      {T('Bytt system')}
    </button>
  );
}

/**
 * The link's refusal, but a missing org number points at the field on this same page: the server's
 * sentence names Innstillinger, which is not where the person stands (onboarding beta test, 2026-11-05).
 */
const linkErrorText = (e: unknown) =>
  e instanceof ApiError && e.code === 'org_number_missing'
    ? T('Fyll inn organisasjonsnummeret øverst på denne siden og trykk Finn. Koble til etterpå.')
    : errorText(e);

/** Go's approval page when the server offers it, else the client key; the same calls as Innstillinger, Lønn. */
function PowerOfficeBox({ status }: { status: PowerOfficeStatus | undefined }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const [key, setKey] = useState('');
  const connect = useMutation({
    mutationFn: () => put<PowerOfficeStatus>('/admin/poweroffice', { key }),
    onSuccess: (s) => {
      qc.setQueryData(KEY(tenant?.id), s);
      setKey('');
    },
    onError: (e) => toast(linkErrorText(e)),
  });
  // Go comes back to Innstillinger, Lønn, which sends a company still in setup back here
  const start = useMutation({
    mutationFn: () => post<{ url: string }>('/admin/poweroffice/onboarding', {}),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast(linkErrorText(e)),
  });
  if (!status) return null;
  if (!status.available)
    return (
      <div className={css.connect}>
        <strong>{T('PowerOffice Go')}</strong>
        <span>
          {T(
            'Den direkte koblingen er ikke slått på ennå. Legg inn ansatte selv nå, og bruk PowerOffice-filen under Timer til lønn.',
          )}
        </span>
      </div>
    );
  if (status.connected)
    return (
      <div className={css.connect} data-testid="setup-po-connected">
        <strong>
          {T('Koblet til PowerOffice Go')}
          {status.client_name ? `: ${status.client_name}` : ''}
        </strong>
        <span>
          {T(
            'Timefly leser ansatte og lønnsarter derfra, og skriver ingenting før du sender timer til lønn.',
          )}
        </span>
      </div>
    );
  return status.onboarding ? (
    <div className={css.connect}>
      <strong>{T('Koble til PowerOffice Go')}</strong>
      <span>
        {T('Du logger inn i PowerOffice Go og godkjenner Timefly for bedriften. Så kommer du tilbake hit.')}
      </span>
      <span>
        <Button disabled={start.isPending} onClick={() => start.mutate()}>
          {T('Koble til PowerOffice Go')}
        </Button>
      </span>
    </div>
  ) : (
    <form
      className={css.connect}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <strong>{T('Koble til PowerOffice Go')}</strong>
      <ol>
        <li>
          {T(
            'I PowerOffice Go: åpne Meny, Innstillinger, Utvidelser. Velg Egendefinert og lim inn applikasjonsnøkkelen du har fått fra Timefly.',
          )}
        </li>
        <li>{T('Kopier klientnøkkelen Go viser før du trykker OK. Go viser den bare én gang.')}</li>
      </ol>
      <div className={css.row}>
        <input
          className={css.input}
          type="password"
          autoComplete="off"
          aria-label={T('Klientnøkkel fra PowerOffice')}
          placeholder={T('Klientnøkkel fra PowerOffice')}
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <Button type="submit" disabled={connect.isPending || key.trim().length < 20}>
          {connect.isPending ? T('Kobler til…') : T('Koble til')}
        </Button>
      </div>
    </form>
  );
}

/** Tripletex's employee key, pasted as on Innstillinger, Lønn (#187). */
function TripletexBox({ name }: { name: string | null }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const [token, setToken] = useState('');
  const connect = useMutation({
    mutationFn: () => put<TripletexStatus>('/admin/tripletex', { token }),
    onSuccess: (s) => {
      qc.setQueryData(TX_KEY(tenant?.id), s);
      setToken('');
    },
    onError: (e) => toast(linkErrorText(e)),
  });
  if (name !== null)
    return (
      <div className={css.connect} data-testid="setup-tx-connected">
        <strong>
          {T('Koblet til Tripletex')}
          {name ? `: ${name}` : ''}
        </strong>
        <span>
          {T(
            'Timefly leser ansatte og lønnsarter derfra, og skriver ingenting før du sender timer til lønn.',
          )}
        </span>
      </div>
    );
  return (
    <form
      className={css.connect}
      onSubmit={(e) => {
        e.preventDefault();
        connect.mutate();
      }}
    >
      <strong>{T('Bruker dere Tripletex? Koble til, så henter vi ansatte og lønnsarter')}</strong>
      <TripletexKeySteps />
      <div className={css.row}>
        <input
          className={css.input}
          type="password"
          autoComplete="off"
          aria-label={T('Nøkkel fra Tripletex')}
          placeholder={T('Nøkkel fra Tripletex')}
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <Button type="submit" disabled={connect.isPending || token.trim().length < 20}>
          {connect.isPending ? T('Kobler til…') : T('Koble til')}
        </Button>
      </div>
    </form>
  );
}

/**
 * «Hvem tilhører denne bedriften?» (OP-04, HUG-02): the payroll system's departments with a checkbox each.
 * Eligibility is the server's (importScope.js): another store's department, one outside the caller's access
 * and an empty one are shown and cannot be ticked. Continue saves the ticks with the version read; that is
 * the confirmation, no extra attestation (owner 2026-11-04). A source department is where people come
 * from, never a Timefly team.
 */
function Scope({ scope, onBack, onDone }: { scope: ImportScope; onBack: () => void; onDone: () => void }) {
  const { tenant } = useTenant();
  const src = usePeopleSource();
  const save = useImportScopeWrite();
  const [ticked, setTicked] = useState<Set<number>>(() => new Set(scope.selected ?? scope.suggested));
  const [err, setErr] = useState<string | null>(null);
  const company = tenant?.brreg_name ?? tenant?.name ?? '';
  const eligible = scope.departments.filter((d) => d.state === 'eligible');
  const people = eligible.filter((d) => ticked.has(d.id)).reduce((n, d) => n + d.people, 0);
  const all = eligible.reduce((n, d) => n + d.people, 0);
  const flip = (id: number) => {
    const s = new Set(ticked);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setTicked(s);
    setErr(null);
  };
  const why: Record<ScopeDepartment['state'], string> = {
    eligible: '',
    other_store: 'Hører til en annen butikk',
    outside: 'Utenfor din tilgang',
    empty: 'Ingen ansatte',
  };
  const chosenIds = eligible.filter((d) => ticked.has(d.id)).map((d) => d.id);
  return (
    <Frame
      title={T('Hvem tilhører denne bedriften?')}
      sub={`${src?.short ?? ''} → ${company}`}
      aside={<ChangeSystem />}
    >
      <div className={css.panel}>
        <h2>{T('Velg kildeavdelinger')}</h2>
        <p className={css.lead}>
          {T(
            'Ta med avdelingene som tilhører {company}. Dette velger hvem vi henter, ikke hvilke team de får.',
            {
              company,
            },
          )}
        </p>
        <p className={css.muted} data-testid="scope-setup-only">
          {T(
            'Valget gjelder bare hvem som hentes nå. Avdelingen lønnskoblingen bruker for lønnsfilen, under Innstillinger, Lønn, endres ikke.',
          )}
        </p>
        {scope.state === 'department_needed' && (
          <p className={css.note} data-testid="scope-department-needed">
            <Icon name="lock" size={16} />
            {T(DEPT_NEEDED)}
          </p>
        )}
        {scope.departments.length === 0 ? (
          <p className={css.muted}>{T('Fant ingen avdelinger i {system}.', { system: src?.short ?? '' })}</p>
        ) : (
          <ul className={css.depts} data-testid="scope-departments">
            {scope.departments.map((d) => {
              const on = d.state === 'eligible';
              return (
                <li key={d.id} data-state={d.state} data-on={(on && ticked.has(d.id)) || undefined}>
                  <input
                    type="checkbox"
                    id={`op-dept-${d.id}`}
                    disabled={!on || save.isPending}
                    checked={on && ticked.has(d.id)}
                    onChange={() => flip(d.id)}
                    aria-describedby={`op-dept-${d.id}-sub`}
                  />
                  <label htmlFor={`op-dept-${d.id}`}>
                    <b>{deptName(d)}</b>
                    <span id={`op-dept-${d.id}-sub`}>
                      {d.people === 1 ? T('1 ansatt') : T('{n} ansatte', { n: d.people })}
                      {d.code && d.id !== 0 ? `. ${T('Avdeling {code}', { code: d.code })}` : ''}
                    </span>
                  </label>
                  {!on && (
                    <span className={css.blocked}>
                      <Icon name="lock" size={14} />
                      {T(why[d.state])}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className={chosenIds.length ? css.figOk : css.figOpen} data-figure data-testid="scope-count">
          {T('{a} av {b} ansatte er valgt.', { a: people, b: all })}
        </p>
        {err && (
          <p className={css.error} role="alert">
            {err}
          </p>
        )}
        <p className={css.muted}>
          {T(
            'Neste: se hva kilden har for de valgte ansatte. Team er valgfritt og settes senere under Ansatte.',
          )}
        </p>
        <div className={css.foot}>
          <Button
            tone="pri"
            disabled={
              save.isPending || (eligible.length > 0 && chosenIds.length === 0) || scope.state !== 'ready'
            }
            onClick={() =>
              save.mutate(
                { departments: chosenIds, version: scope.version },
                { onSuccess: onDone, onError: (e) => setErr(errorText(e)) },
              )
            }
          >
            {save.isPending ? T('Lagrer…') : T('Se hva vi kan hente')}
          </Button>
          <button type="button" className={css.ghost} onClick={onBack}>
            {T('Tilbake')}
          </button>
          {eligible.length > 0 && chosenIds.length === 0 && (
            <span className={css.muted}>{T('Velg minst én avdeling.')}</span>
          )}
        </div>
      </div>
    </Frame>
  );
}

/**
 * A payroll system that refused Timefly's key or privilege (its 401 or 403, which services/poweroffice.js and
 * tripletex.js answer as `…_denied`): access is missing, which the administrator fixes in that system, not a
 * failure a retry helps (OP-05, HUG-03).
 */
const DENIED = ['poweroffice_denied', 'tripletex_denied'];
const denied = (e: unknown) => e instanceof ApiError && DENIED.includes(e.code ?? '');

type Lookup = 'available' | 'saved' | 'unsupported' | 'permission' | 'empty' | 'failed' | 'unchecked';
/** a static badge under the description: the words carry the state, the colour supports it (OP-05) */
function Badge({ state, label }: { state: Lookup; label?: string }) {
  const tone =
    state === 'saved' ? 'ok' : state === 'permission' ? 'warn' : state === 'failed' ? 'dgr' : 'neu';
  const words: Record<Lookup, string> = {
    available: 'Klar til import',
    saved: 'Lagret',
    unsupported: 'Støttes ikke',
    permission: 'Mangler tilgang',
    empty: 'Ingen funnet',
    failed: 'Kunne ikke hentes',
    unchecked: 'Ikke sjekket',
  };
  const icon: IconName | null =
    state === 'available' || state === 'unchecked' || state === 'empty'
      ? 'info'
      : state === 'unsupported'
        ? 'lock'
        : null;
  return (
    <span data-lookup={state}>
      <Status tone={tone} {...(icon ? { icon } : {})}>
        {label ?? T(words[state])}
      </Status>
    </span>
  );
}
interface Row {
  key: string;
  icon: IconName;
  title: string;
  desc: string;
  state: Lookup;
  label?: string;
  action?: { label: string; run: () => void };
}

/**
 * What the administrator deliberately left out of the import, kept on the import draft by key (n<number>,
 * u<user id>, regular, pt<code>): a row whose rest was imported and whose remainder was left out counts as
 * done, so «Fullfør resten med AI» is reachable (onboarding beta 2026-11-05). `save` keeps the keys another
 * screen owns and sets this screen's.
 */
function useLeftOut() {
  const { draft, kilde } = useSetup();
  const write = useSetupDraftWrite();
  const now = (draft?.fields as ImportFields | null | undefined)?.valgt_bort ?? [];
  const save = async (mine: (k: string) => boolean, off: string[]) => {
    const next = [...now.filter((k) => !mine(k)), ...off];
    if ([...next].sort().join() === [...now].sort().join()) return;
    try {
      await write.mutateAsync({
        setup: 'import',
        version: draft?.version ?? 0,
        fields: { ...(draft || !kilde ? {} : { kilde }), valgt_bort: next },
      });
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
    }
  };
  return { left: new Set(now), save };
}
const PERSON_KEY = (k: string) => k.startsWith('n') || k.startsWith('u');
const PAY_KEY = (k: string) => k === 'regular' || k.startsWith('pt');

/**
 * What the source has for this company (OP-05, HUG-03), read now: one row per kind with its state as a
 * badge below the description and the action in its own column. Found is not saved: «Klar til import»
 * until the administrator imports it. A retry asks again; a change of departments re-reads the counts.
 */
function Oversikt({ scope, onView }: { scope: ImportScope | undefined; onView: (v: View) => void }) {
  const { tenant } = useTenant();
  const { go, busy, kilde, sys, draft, receipt } = useSetup();
  const src = usePeopleSource();
  const link = useLink(src);
  const payLink = useLink(sys);
  const connected = !!link?.connected;
  const m = useMatch(src, connected);
  const paySide = useMatch(sys, !!payLink?.connected && sys?.key !== src?.key);
  const pm = src ? m : paySide;
  const sug = useQuery({
    queryKey: ['poweroffice-pay-types'],
    queryFn: () => get<{ suggestions: PayTypeSuggestion[] }>('/admin/poweroffice/pay-types'),
    enabled: !!payLink?.connected && sys?.key === 'poweroffice',
  });
  const users = useUsers();
  const nav = useNavigate();
  const write = useSetupDraftWrite();
  const [later, setLater] = useState(false);
  const [date, setDate] = useState('');
  const company = tenant?.brreg_name ?? tenant?.name ?? '';
  const selected = scope?.selected ?? null;
  const inScope = (dep: number | null | undefined) => selected == null || selected.includes(dep ?? 0);
  const names = (scope?.departments ?? [])
    .filter((d) => selected?.includes(d.id))
    .map((d) => deptName(d))
    .join(', ');
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;
  // Microsoft's answer when its sign-in was started here and refused (?microsoft=consent, scope, …)
  const msBack = useSearchParams()[0].get('microsoft');
  const msOn = useMicrosoftOn();
  const { left } = useLeftOut();

  const rows: Row[] = [];
  // the people
  if (src) {
    const missing = (m.data?.missing ?? []).filter((x) => inScope(x.department_id));
    const linkable = (m.data?.people ?? []).filter(
      (p) => p.sure && p.suggestion && p.suggestion !== p.current && inScope(p.department_id),
    );
    const here = (m.data?.people ?? []).filter((p) => p.current && inScope(p.department_id));
    // one the source lacks a birth date for cannot be made here: it waits for the source, not for a press
    const addable = missing.filter((x) => !!x.existing || !!(x.birth_date && x.name && x.lastname));
    const blocked = missing.length - addable.length;
    // one deliberately left out on Se over ansatte is not waiting for anything
    const ready = addable.filter((x) => !left.has(`n${x.number}`));
    const toLink = linkable.filter((p) => !left.has(`u${p.user_id}`));
    const leftN = addable.length - ready.length + linkable.length - toLink.length;
    const n = ready.length + toLink.length;
    const total = missing.length + toLink.length + here.length;
    const row: Row = { key: 'ansatte', icon: 'users', title: T('Ansatte'), desc: '', state: 'unchecked' };
    if (!link) row.state = 'unchecked';
    else if (!link.available) {
      row.state = 'unsupported';
      row.desc = T('Den direkte koblingen er ikke slått på ennå. Legg inn ansatte selv eller fra en fil.');
    } else if (!connected) {
      row.state = 'permission';
      row.label = T('Ikke koblet til');
      row.desc = T('Koble til {system} for å se hvem som kan hentes.', { system: src.short });
      row.action = { label: T('Koble til'), run: () => onView('bedrift') };
    } else if (m.isError && denied(m.error)) {
      row.state = 'permission';
      row.desc = errorText(m.error);
      row.action = { label: T('Koble til på nytt'), run: () => nav('/innstillinger/lonn') };
    } else if (m.isError) {
      row.state = 'failed';
      row.desc = errorText(m.error);
      row.action = { label: T('Prøv igjen'), run: () => void m.refetch() };
    } else if (m.isPending) {
      row.desc = T('Henter fra {system} …', { system: src.short });
    } else if (total === 0) {
      row.state = 'empty';
      row.desc = T('Fant ingen ansatte med ansattnummer i de valgte avdelingene.');
      row.action = { label: T('Hent på nytt'), run: () => void m.refetch() };
    } else if (n > 0) {
      row.state = 'available';
      row.desc =
        n === 1
          ? T('1 person kan hentes. Navn, ansattnummer, lønn og kontrakt kan gjennomgås.')
          : T('{n} personer kan hentes. Navn, ansattnummer, lønn og kontrakt kan gjennomgås.', { n });
      row.action = { label: T('Se over ansatte'), run: () => go({ setup_step: 'employees' }) };
    } else {
      // a person the source lacks a birth date for is an actual gap: amber, the figure says how many are in
      row.state = blocked ? 'permission' : 'saved';
      row.label = T('{a} av {b} lagt til', { a: here.length, b: total });
      row.desc = blocked
        ? T('{n} mangler fødselsdato i {system} og kan hentes når den er lagt inn der.', {
            n: blocked,
            system: src.short,
          })
        : leftN
          ? T('{n} valgt bort. Resten er i Timefly.', { n: leftN })
          : T('Alle i de valgte avdelingene er i Timefly.');
      row.action = { label: T('Se over ansatte'), run: () => go({ setup_step: 'employees' }) };
    }
    rows.push(row);
    if (connected && scope && scope.state !== 'unsupported' && scope.state !== 'not_connected')
      rows.push({
        key: 'avdelinger',
        icon: 'users',
        title: T('Kildeavdelinger'),
        desc: names
          ? T('{names}. Avgrenser hvem vi henter til {company}. Team velges separat.', { names, company })
          : T('Ikke valgt ennå.'),
        state: selected ? 'saved' : 'available',
        label: selected ? T('Gjennomgått') : T('Velg avdelinger'),
        action: { label: T('Endre'), run: () => onView('avdelinger') },
      });
  } else if (kilde === 'microsoft' && msBack && msBack !== 'ok') {
    // Microsoft's refusal, back from the sign-in this row started: missing access apart from a failure
    rows.push({
      key: 'ansatte',
      icon: 'users',
      title: T('Ansatte'),
      desc: T(MS_ERRORS[msBack] ?? (MS_ERRORS.graph as string)),
      state: MS_DENIED.includes(msBack) ? 'permission' : 'failed',
      action: {
        label: T('Prøv igjen'),
        run: () => void startMicrosoft('oppsett').catch((e) => toast(errorText(e))),
      },
    });
  } else if (kilde === 'microsoft' && !msOn.isPending && msOn.data?.available !== true) {
    // not set up on this server: said so, with the ways that work, never a button that is refused
    rows.push({
      key: 'ansatte',
      icon: 'users',
      title: T('Ansatte'),
      desc: T(MS_OFF),
      state: 'unsupported',
      label: T('Ikke tilgjengelig'),
      action: { label: T('Hent fra fil'), run: () => nav('/ansatte/importer') },
    });
  } else if (kilde === 'microsoft') {
    // the people in the company now are not all from Microsoft: the line says how many, no «lagt til» badge
    rows.push({
      key: 'ansatte',
      icon: 'users',
      title: T('Ansatte'),
      desc: people
        ? T('{n} i bedriften nå. Logg inn hos Microsoft for å hente flere fra katalogen.', { n: people })
        : T(
            'Logg inn hos Microsoft. En administrator i Microsoft 365 må ha godkjent at Timefly leser brukerne.',
          ),
      state: people ? 'saved' : 'unchecked',
      label: people ? '' : T('Krever innlogging'),
      ...(msOn.isPending
        ? {}
        : {
            action: {
              label: T('Hent fra Microsoft 365'),
              run: () => void startMicrosoft('oppsett').catch((e) => toast(errorText(e))),
            },
          }),
    });
  } else {
    // a file, by hand, or Visma, which has no direct link
    rows.push({
      key: 'ansatte',
      icon: 'users',
      title: T('Ansatte'),
      desc:
        kilde === 'visma'
          ? T('Visma har ingen direkte kobling i Timefly. Hent fra Excel eller CSV, eller legg inn selv.')
          : people
            ? T('{n} i bedriften nå. Ingen har fått e-post.', { n: people })
            : kilde === 'fil'
              ? T('Velg filen. Du ser hver person før noen legges til.')
              : T('Legg inn den første ansatte. De får ingen e-post før du selv sender velkomsten.'),
      state: kilde === 'visma' ? 'unsupported' : people ? 'saved' : 'available',
      // the company's count is everyone, not what this source added: no «lagt til» badge (beta 2026-11-05)
      label: kilde === 'visma' ? T('Ingen direkte kobling') : people ? '' : T('Klar'),
      action:
        kilde === 'manuell'
          ? { label: T('Legg til en ansatt'), run: () => nav('/ansatte/ny?fra=oppsett') }
          : { label: T('Hent fra fil'), run: () => nav('/ansatte/importer') },
    });
  }
  // the pay types: from the payroll system's link, whatever the people came from
  {
    const regular = pm.data?.types?.find((t) => t.key === 'regular');
    const all = sug.data?.suggestions ?? [];
    const list = all.filter((x) => !left.has(`pt${x.code}`));
    const regularOpen = !!regular?.suggestion && regular.suggestion !== regular.current;
    const open = (regularOpen && !left.has('regular') ? 1 : 0) + list.length;
    // pay items deliberately left out on Se over lønnsarter: decided, not waiting (beta 2026-11-05)
    const leftPay = (regularOpen && left.has('regular') ? 1 : 0) + all.length - list.length;
    const row: Row = {
      key: 'lonnsarter',
      icon: 'document',
      title: T('Lønnsarter'),
      desc: '',
      state: 'unchecked',
    };
    if (!sys) {
      row.state = 'unsupported';
      row.desc = T(
        'Lønnsarter hentes bare fra PowerOffice Go og Tripletex. Sett dem under Innstillinger, Lønn.',
      );
    } else if (!payLink) row.state = 'unchecked';
    else if (!payLink.available) {
      row.state = 'unsupported';
      row.desc = T('Den direkte koblingen er ikke slått på ennå.');
    } else if (!payLink.connected) {
      row.state = 'permission';
      row.label = T('Ikke koblet til');
      row.desc = T('Koble til {system} for å hente lønnsartene.', { system: sys.short });
      if (src) row.action = { label: T('Koble til'), run: () => onView('bedrift') };
    } else if (denied(pm.error ?? sug.error)) {
      row.state = 'permission';
      row.desc = errorText(pm.error ?? sug.error);
      row.action = { label: T('Koble til på nytt'), run: () => nav('/innstillinger/lonn') };
    } else if (pm.isError || sug.isError) {
      row.state = 'failed';
      row.desc = errorText(pm.error ?? sug.error);
      row.action = {
        label: T('Prøv igjen'),
        run: () => {
          void pm.refetch();
          void sug.refetch();
        },
      };
    } else if (pm.isPending || (sys.key === 'poweroffice' && sug.isPending)) {
      row.desc = T('Leser lønnsartene …');
    } else if (open > 0) {
      row.state = 'available';
      row.desc =
        open === 1
          ? T('1 lønnsart funnet. Kontroller hva den skal brukes til.')
          : T('{n} lønnsarter funnet. Kontroller hva de skal brukes til.', { n: open });
      row.action = { label: T('Se over lønnsarter'), run: () => go({ setup_step: 'rules' }) };
    } else if (regular?.current || leftPay) {
      row.state = 'saved';
      row.desc = [
        regular?.current
          ? T('Vanlige timer har kode {code}. Satsene kommer fra lønnen til hver person.', {
              code: regular.current,
            })
          : '',
        leftPay ? T('{n} valgt bort.', { n: leftPay }) : '',
      ]
        .filter(Boolean)
        .join(' ');
    } else {
      row.state = 'empty';
      row.desc = T('Fant ingen lønnsart for timelønn i {system}.', { system: sys.short });
    }
    rows.push(row);
  }
  const next = rows.find((r) => r.state === 'available' && r.action && r.key !== 'avdelinger');
  // gjennomgatt is written once the administrator goes on to Hugin after the review (HUG-07)
  const draftVersion = draft?.version ?? 0;
  const base = draft ? {} : kilde ? { fields: { kilde } } : {};
  async function toAI() {
    try {
      await write.mutateAsync({
        setup: 'import',
        version: draftVersion,
        fields: { ...(draft || !kilde ? {} : { kilde }), gjennomgatt: true },
      });
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
      return;
    }
    await go({ setup_step: 'hugin' });
  }
  async function defer() {
    try {
      await write.mutateAsync({ setup: 'import', version: draftVersion, ...base, remind_on: date });
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
      return;
    }
    await go({ setup_step: 'hugin' });
  }
  // a date put off earlier says nothing once the import is done (beta 2026-11-05)
  const remind = draft?.status === 'done' ? null : draft?.remind_on;
  return (
    <Frame
      title={T('Hent det dere allerede har')}
      sub={T('Se hva vi fant for {company}, og velg hva som skal importeres.', { company })}
      aside={<ChangeSystem />}
    >
      {receipt && (
        <p className={css.receipt} tabIndex={-1} data-testid="setup-receipt">
          <span className={css.okDot} aria-hidden="true">
            <Icon name="check" size={14} strokeWidth={2.4} />
          </span>
          {receipt}
        </p>
      )}
      <h2 className={css.h2}>{T('Dette fant vi i {source}', { source: nameOf(kilde) || T('kilden') })}</h2>
      <ul className={css.lookup} data-testid="setup-lookup">
        {rows.map((r) => (
          <li key={r.key} data-row={r.key}>
            <span className={css.lookupIcon} aria-hidden="true">
              <Icon name={r.icon} size={18} />
            </span>
            <div className={css.lookupText}>
              <b>{r.title}</b>
              {r.desc && <span>{r.desc}</span>}
              {r.label !== '' && <Badge state={r.state} {...(r.label ? { label: r.label } : {})} />}
            </div>
            <div className={css.lookupAct}>
              {r.action && (
                <button type="button" className={css.textLink} disabled={busy} onClick={r.action.run}>
                  {r.action.label}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className={css.muted}>
        {T('Det som finnes, avhenger av bedriften, modulene og tilgangen i kilden. Funnet er ikke lagret.')}
      </p>
      <div className={css.nextBox} data-testid="setup-next">
        <b>
          {next
            ? T('Neste: {what}', { what: next.action?.label.toLowerCase() ?? '' })
            : T('Neste: fullfør med AI')}
        </b>
        <span>
          {next
            ? T('Du kommer tilbake hit etter importen.')
            : T('Hugin hjelper med det som mangler, som åpningstider, vakttyper og bemanning.')}
        </span>
        {remind && (
          <span className={css.muted} data-testid="setup-deferred">
            {T('Resten av importen er satt til {date}. Den er ikke ferdig.', { date: fmtD(remind) })}
          </span>
        )}
        {later ? (
          <form
            className={css.later}
            onSubmit={(e) => {
              e.preventDefault();
              if (date) void defer();
            }}
          >
            <label htmlFor="oppsett-later">{T('Minn meg på importen')}</label>
            <DateInput id="oppsett-later" className="inp" value={date} onValueChange={setDate} />
            <Button type="submit" tone="pri" disabled={!date || busy || write.isPending}>
              {T('Lagre og gå videre')}
            </Button>
            <button type="button" className={css.ghost} onClick={() => setLater(false)}>
              {T('Avbryt')}
            </button>
          </form>
        ) : (
          <div className={css.foot}>
            {next ? (
              <Button tone="pri" disabled={busy} onClick={next.action?.run}>
                {next.action?.label}
                <Icon name="arrowRight" size={16} />
              </Button>
            ) : (
              <Button tone="pri" disabled={busy || write.isPending} onClick={toAI}>
                {T('Fullfør resten med AI')}
                <Icon name="arrowRight" size={16} />
              </Button>
            )}
            <button
              type="button"
              className={css.ghost}
              onClick={() => setLater(true)}
              data-testid="setup-later"
            >
              {T('Ta resten av importen senere')}
            </button>
          </div>
        )}
      </div>
    </Frame>
  );
}

/**
 * What Se over ansatte saved, said apart: made or added from another company, linked to someone already
 * here, and who was mailed (beta 2026-11-05: «5 ansatte er lagt til» when 5 were linked and none made).
 */
export function receiptOf(added: number, linked: number, told: number, system: string) {
  const what =
    added && linked
      ? T('{a} lagt til og {b} koblet fra {system}.', { a: added, b: linked, system })
      : added
        ? added === 1
          ? T('1 ansatt er lagt til fra {system}.', { system })
          : T('{n} ansatte er lagt til fra {system}.', { n: added, system })
        : linked === 1
          ? T('1 ansatt fra før er koblet til {system}.', { system })
          : T('{n} ansatte fra før er koblet til {system}.', { n: linked, system });
  const mail = told
    ? T('{n} med Timefly fra før har fått beskjed på e-post. Ingen andre har fått e-post.', { n: told })
    : T('Ingen har fått e-post.');
  return `${what} ${mail}`;
}

/**
 * Se over ansatte (HUG-04): the people in the chosen departments. Chosen, left out by the administrator,
 * already here, outside the departments and refused stay apart. The ones already here are linked on the
 * server's sure suggestion and the rest made as on Koble ansatte (addFromGo, no mail); then back to what
 * was found, with a receipt of what was saved.
 */
function People() {
  const { go, busy, sys, setReceipt } = useSetup();
  const { mode } = useTenant();
  const connected = !!useLink(sys)?.connected;
  const users = useUsers();
  const nav = useNavigate();
  const qc = useQueryClient();
  const m = useMatch(sys, connected);
  const scope = useImportScope(!!sys && connected);
  const selected = scope.data?.selected ?? null;
  const inScope = (dep: number | null | undefined) => selected == null || selected.includes(dep ?? 0);
  const deptOf = (dep: number | null | undefined) =>
    deptName(scope.data?.departments?.find((d) => d.id === (dep ?? 0)));
  const all = m.data?.people ?? [];
  const link = all.filter(
    (p) => p.sure && p.suggestion && p.suggestion !== p.current && inScope(p.department_id),
  );
  const linked = all.filter((p) => p.current && inScope(p.department_id));
  const missingAll = m.data?.missing ?? [];
  const missing = missingAll.filter((x) => inScope(x.department_id));
  const outside =
    missingAll.length - missing.length + all.filter((p) => p.current && !inScope(p.department_id)).length;
  const addable = (x: Missing) => !!x.existing || !!(x.birth_date && x.name && x.lastname);
  const leftOut = useLeftOut();
  // the ones left out last time stay unticked
  const [off, setOff] = useState<Set<string>>(() => new Set([...leftOut.left].filter(PERSON_KEY)));
  // number → the server's reason, for people the create refused
  const [refused, setRefused] = useState<Record<string, string>>({});
  const on = (k: string) => !off.has(k);
  const flip = (k: string) => {
    const s = new Set(off);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setOff(s);
  };
  const toAdd = missing.filter((x) => addable(x) && !refused[x.number] && on(`n${x.number}`));
  const toLink = link.filter((p) => on(`u${p.user_id}`));
  const offKeys = [
    ...link.filter((p) => !on(`u${p.user_id}`)).map((p) => `u${p.user_id}`),
    ...missing.filter((x) => addable(x) && !on(`n${x.number}`)).map((x) => `n${x.number}`),
  ];
  // someone with Timefly through another company is told by e-mail when added (payrollPeople.js joinFromStore)
  const told = toAdd.filter((x) => x.existing?.account).length;
  const save = useMutation({
    mutationFn: async () => {
      if (toLink.length)
        await put(`${sys?.base}/match`, {
          people: toLink.map((p) => ({ user_id: p.user_id, number: p.suggestion })),
          types: [],
        });
      return toAdd.length
        ? addFromGo(toAdd, sys ?? POWEROFFICE, true)
        : { made: 0, no: {} as Record<string, string> };
    },
    onSuccess: async ({ no }) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: [sys?.key, 'match'] });
      // a refused person keeps their row with the reason and is left out of the next press, so the
      // rest goes on (review 2026-10-19: «added first user and stopped»)
      const failed = Object.keys(no).length;
      if (failed) {
        setRefused((r) => ({ ...r, ...no }));
        toast(T('{n} kunne ikke legges til. Se grunnen på raden.', { n: failed }));
        return;
      }
      setReceipt(receiptOf(toAdd.length, toLink.length, told, sys?.short ?? ''));
      await leftOut.save(PERSON_KEY, offKeys);
      await go({ setup_step: 'company' });
    },
    onError: (e) => toast(errorText(e)),
  });
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;
  const back = () => go({ setup_step: 'company' });

  if (!connected)
    return (
      <Frame title={T('Ansatte')} aside={<ChangeSystem />}>
        <div className={css.panel}>
          <p className={css.lead}>
            {T('Legg til de ansatte under Ansatte. De får ingen e-post før du selv sender velkomsten.')}{' '}
            {mode === 'timer'
              ? T('Legg inn timelønn når du legger til folk, så blir lønnskostnaden riktig.')
              : T('Legg inn timelønn når du legger til folk, så blir lønnskostnaden i vaktplanen riktig.')}
          </p>
          {sys && (
            // the key skipped: say where it goes later, so the step is not a dead end (#191, run A)
            <p className={css.muted} data-testid="setup-connect-later">
              {T('Koble til {system} senere under Innstillinger, Lønn, så henter vi de ansatte derfra.', {
                system: sys.short,
              })}
            </p>
          )}
          <p className={css.muted} data-testid="setup-people">
            {people === 1 ? T('1 person i bedriften nå.') : T('{n} personer i bedriften nå.', { n: people })}
          </p>
          <span>
            <Button onClick={() => nav('/ansatte/ny?fra=oppsett')}>{T('Legg til en ansatt')}</Button>
          </span>
          <div className={css.foot}>
            <Button tone="pri" disabled={busy} onClick={back}>
              {T('Tilbake til oversikten')}
            </Button>
          </div>
        </div>
      </Frame>
    );
  type R = {
    k: string;
    name: string;
    sub: string;
    dept: string;
    chip: [string, 'ok' | 'neu' | 'warn' | 'dgr' | 'act'];
    box: boolean | null;
    told?: boolean;
  };
  const rows: R[] = [
    ...linked.map((p) => ({
      k: `c${p.user_id}`,
      name: p.name,
      sub: `${T('Ansattnr')} ${p.current}`,
      dept: deptOf(p.department_id),
      chip: [T('Finnes fra før'), 'neu'] as R['chip'],
      box: null,
    })),
    ...link.map((p) => ({
      k: `u${p.user_id}`,
      name: p.name,
      sub: `${T('Ansattnr')} ${p.suggestion}`,
      dept: deptOf(p.department_id),
      chip: [
        on(`u${p.user_id}`)
          ? T('Kobles, {why}', { why: T(BASIS[p.basis ?? 'name']?.[0] ?? '').toLowerCase() })
          : T('Valgt bort'),
        on(`u${p.user_id}`) ? 'act' : 'neu',
      ] as R['chip'],
      box: true,
    })),
    ...missing.map((x) => {
      const k = `n${x.number}`;
      const chip: R['chip'] = refused[x.number]
        ? [T('Kunne ikke legges til: {why}', { why: refused[x.number] ?? '' }), 'dgr']
        : !addable(x)
          ? [T('Mangler fødselsdato i {system}', { system: sys?.short ?? '' }), 'warn']
          : !on(k)
            ? [T('Valgt bort'), 'neu']
            : x.existing?.account
              ? [T('Har Timefly fra før, får beskjed på e-post'), 'act']
              : x.existing
                ? [T('Finnes i {stores}', { stores: x.existing.stores.join(', ') }), 'act']
                : [T('Ny ansatt'), 'act'];
      return {
        k,
        name: `${x.name} ${x.lastname}`.trim(),
        sub: `${T('Ansattnr')} ${x.number}${x.email ? `, ${x.email}` : ''}`,
        dept: deptOf(x.department_id),
        chip,
        box: addable(x) && !refused[x.number],
        told: !!x.existing?.account,
      };
    }),
  ];
  const chosenN = toAdd.length + toLink.length;
  const offN = [
    ...link.filter((p) => !on(`u${p.user_id}`)),
    ...missing.filter((x) => addable(x) && !on(`n${x.number}`)),
  ].length;
  return (
    <Frame
      title={T('Se over ansatte')}
      sub={T('Velg hvem som skal tas med, og bekreft importen. Du kommer tilbake til oversikten etterpå.')}
      aside={<ChangeSystem />}
    >
      <div className={css.panel}>
        <div className={css.sourceHead}>
          <strong>{T('Ansatte fra {system}', { system: sys?.short ?? '' })}</strong>
          <span className={css.sourceSub}>
            {T(
              'De som allerede finnes i Timefly kobles. Resten opprettes med ansattnummer, lønn og kontrakt. Nye ansatte får ingen e-post.',
            )}
            {rows.some((r) => r.told) &&
              ` ${T('De som har Timefly fra før, får beskjed på e-post når de legges til.')}`}
          </span>
        </div>
        {m.isPending ? (
          <output className={css.muted}>{T('Henter fra {system} …', { system: sys?.short ?? '' })}</output>
        ) : m.isError ? (
          <p className={css.error}>{errorText(m.error)}</p>
        ) : rows.length === 0 ? (
          // nothing to fetch said in words, not a blank step (walk-through 2026-10-21, #191 F2)
          <div className={css.muted} data-testid="setup-people-none">
            <p>
              {T(
                'Fant ingen ansatte å hente fra {system}. Bare ansatte med ansattnummer der kommer med. Gi dem ansattnummer i {system} og hent på nytt, eller legg dem inn i Timefly senere.',
                { system: sys?.short ?? '' },
              )}
            </p>
            <Button disabled={m.isFetching} onClick={() => void m.refetch()}>
              {m.isFetching ? T('Henter…') : T('Hent på nytt')}
            </Button>
          </div>
        ) : (
          <>
            <p className={css.figLine} data-testid="setup-people-count">
              <span className={chosenN ? css.figOk : css.figOpen} data-figure>
                {T('{a} av {b} valgt for import.', { a: chosenN, b: chosenN + offN })}
              </span>{' '}
              {T('{n} finnes fra før.', { n: linked.length + link.length })}
              {outside > 0 ? ` ${T('{n} utenfor de valgte avdelingene.', { n: outside })}` : ''}
            </p>
            <ul className={css.list} data-testid="setup-po-people">
              {rows.map((r) => (
                <li key={r.k}>
                  {r.box === null ? (
                    <span className={css.boxGap} />
                  ) : (
                    <input
                      type="checkbox"
                      id={`op-${r.k}`}
                      disabled={!r.box}
                      checked={!!r.box && on(r.k)}
                      onChange={() => flip(r.k)}
                    />
                  )}
                  <span className={css.av} aria-hidden="true">
                    {initials(r.name)}
                  </span>
                  <label htmlFor={`op-${r.k}`}>
                    <b>{r.name}</b>
                    <span>
                      {r.sub}
                      {r.dept ? `. ${T('Kildeavdeling: {name}', { name: r.dept })}` : ''}
                    </span>
                  </label>
                  <Status tone={r.chip[1]}>{r.chip[0]}</Status>
                </li>
              ))}
            </ul>
          </>
        )}
        {m.data?.department_needed && <p className={css.muted}>{T(DEPT_NEEDED)}</p>}
        <p className={css.muted}>
          {T(
            'Å legge til ansatte registrerer ingen timer og sender ingen invitasjon. Team kan settes senere under Ansatte.',
          )}
        </p>
        <div className={css.foot}>
          <Button
            tone="pri"
            disabled={busy || save.isPending}
            onClick={() =>
              chosenN
                ? save.mutate()
                : // everyone unticked on purpose: that choice is kept, then back
                  void leftOut.save(PERSON_KEY, offKeys).then(back)
            }
          >
            {save.isPending
              ? T('Lagrer…')
              : chosenN
                ? T('Opprett {a} og koble {b}', { a: toAdd.length, b: toLink.length })
                : offN
                  ? T('Lagre valget')
                  : T('Tilbake til oversikten')}
          </Button>
          {chosenN > 0 && (
            <button type="button" className={css.ghost} disabled={busy} onClick={back}>
              {T('Tilbake til oversikten')}
            </button>
          )}
        </div>
      </div>
    </Frame>
  );
}

/**
 * Se over lønnsarter (HUG-04): Vanlige timer's code from the source's suggestion, and Go's supplements and
 * overtime that no pay type here uses yet, made as on Koble ansatte og lønnsarter. A code taken from the
 * source is a code, not a rule: when a supplement applies is set in Innstillinger, Lønn. An item over 100 %
 * (base and supplement, «Overtid 50%» at 150 %) is made as a type that pays the whole hour, at its
 * supplement (#183, owner 2026-10-19).
 */
function Rules() {
  const { go, busy, sys, setReceipt } = useSetup();
  const connected = !!useLink(sys)?.connected;
  const qc = useQueryClient();
  const m = useMatch(sys, connected);
  // Go's supplements and overtime with their rates; Tripletex's salary types carry none (#187), so
  // there only Vanlige timer's code is taken here and the supplements wait for Innstillinger, Lønn
  const sug = useQuery({
    queryKey: ['poweroffice-pay-types'],
    queryFn: () => get<{ suggestions: PayTypeSuggestion[] }>('/admin/poweroffice/pay-types'),
    enabled: connected && sys?.key === 'poweroffice',
  });
  const regular = m.data?.types?.find((t) => t.key === 'regular');
  const regularItem = m.data?.pay_items?.find((i) => i.code === regular?.suggestion);
  const list = sug.data?.suggestions ?? [];
  const [rate, setRate] = useState<Record<string, number>>({});
  // ticked by the person, else by default: Vanlige timer, and an item Go gives a rate; a kroner item
  // without one waits for its rate (typing one ticks it)
  const [pick, setPick] = useState<Record<string, boolean>>({});
  const leftOut = useLeftOut();
  // one left out last time stays unticked
  const isOn = (k: string, dflt: boolean) =>
    pick[k] ?? (leftOut.left.has(k === 'regular' ? k : `pt${k}`) ? false : dflt);
  const regularOpen = !!regular?.suggestion && regular.suggestion !== regular.current;
  const takeRegular = regularOpen && isOn('regular', true);
  const picked = list.filter((x) => isOn(x.code, x.rate_value > 0));
  const offKeys = [
    ...(regularOpen && !takeRegular ? ['regular'] : []),
    ...list.filter((x) => !picked.includes(x)).map((x) => `pt${x.code}`),
  ];
  const rateOf = (x: PayTypeSuggestion) => rate[x.code] ?? x.rate_value;
  // a kroner supplement at 0 kr would send its hours unpaid: it needs its rate first (owner 2026-10-19)
  const zero = picked.filter((x) => x.calculation_type === 'extra_per_hour' && !(rateOf(x) > 0));
  const n = picked.length + (takeRegular ? 1 : 0);
  const save = useMutation({
    mutationFn: async () => {
      if (takeRegular)
        await put(`${sys?.base}/match`, {
          people: [],
          types: [{ key: 'regular', code: regular?.suggestion }],
        });
      for (const x of picked)
        await post('/admin/payroll/types', {
          name: x.name,
          code: x.code,
          calculation_type: x.calculation_type,
          rate_value: rateOf(x),
          ...(x.pays_whole_hour ? { pays_whole_hour: true } : {}),
        });
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['payroll-types'] });
      qc.invalidateQueries({ queryKey: ['poweroffice-pay-types'] });
      qc.invalidateQueries({ queryKey: [sys?.key, 'match'] });
      setReceipt(
        n === 1
          ? T('1 lønnsart er lagret. Tilleggene gjelder først når du har satt når de gjelder.')
          : T('{n} lønnsarter er lagret. Tilleggene gjelder først når du har satt når de gjelder.', { n }),
      );
      await leftOut.save(PAY_KEY, offKeys);
      await go({ setup_step: 'company' });
    },
    onError: (e) => toast(errorText(e)),
  });
  const flip = (k: string, now: boolean) => setPick({ ...pick, [k]: !now });
  const back = () => go({ setup_step: 'company' });
  if (!connected)
    return (
      <Frame title={T('Lønnsarter og tillegg')} aside={<ChangeSystem />}>
        <div className={css.panel}>
          <p className={css.lead}>
            {T(
              'Tillegg for kveld, helg og overtid, og lønnsartene fra lønnssystemet, settes under Innstillinger, Lønn. Det kan vente til første lønn.',
            )}
          </p>
          <div className={css.foot}>
            <Button tone="pri" disabled={busy} onClick={back}>
              {T('Tilbake til oversikten')}
            </Button>
          </div>
        </div>
      </Frame>
    );
  return (
    <Frame
      title={T('Se over lønnsarter')}
      sub={T('Bekreft kodene fra kilden. Deretter hjelper Hugin med oppsettet som mangler.')}
      aside={<ChangeSystem />}
    >
      <div className={css.panel}>
        <div className={css.sourceHead}>
          <strong>{T('Lønnsarter fra {system}', { system: sys?.short ?? '' })}</strong>
          <Status tone="act">{T('Forslag, ikke lagret')}</Status>
        </div>
        <p className={css.lead}>
          {list.length
            ? T(
                'Dette er lønnsartene {system} har for timelønn, tillegg og overtid. Satsen per person kommer fra lønnen deres.',
                { system: sys?.short ?? '' },
              )
            : T(
                'Dette er lønnsarten {system} har for timelønn. Satsen per person kommer fra lønnen deres. Tillegg og overtid settes under Innstillinger, Lønn.',
                { system: sys?.short ?? '' },
              )}
        </p>
        {m.isPending || (sys?.key === 'poweroffice' && sug.isPending) ? (
          <output className={css.muted}>{T('Leser lønnsartene …')}</output>
        ) : (
          <div className={css.rules} data-testid="setup-po-rules">
            {regular?.suggestion && (
              <div className={css.rule}>
                <div className={css.ruleTop}>
                  <input
                    type="checkbox"
                    id="op-regular"
                    disabled={regular.suggestion === regular.current}
                    checked={regular.suggestion === regular.current || isOn('regular', true)}
                    onChange={() => flip('regular', isOn('regular', true))}
                  />
                  <span className={css.code}>{regular.suggestion}</span>
                  <label htmlFor="op-regular">{regularItem?.name ?? T('Timelønn')}</label>
                  <span className={css.muted}>{T('Vanlige timer')}</span>
                </div>
              </div>
            )}
            {list.map((x) => (
              <div key={x.code} className={css.rule}>
                <div className={css.ruleTop}>
                  <input
                    type="checkbox"
                    id={`op-pt-${x.code}`}
                    checked={isOn(x.code, x.rate_value > 0)}
                    onChange={() => flip(x.code, isOn(x.code, x.rate_value > 0))}
                  />
                  <span className={css.code}>{x.code}</span>
                  <label htmlFor={`op-pt-${x.code}`}>{x.name}</label>
                  <input
                    className={css.rate}
                    inputMode="decimal"
                    aria-label={T('Sats for {name}', { name: x.name })}
                    value={String(rateOf(x)).replace('.', ',')}
                    onChange={(e) => {
                      const v = Number(e.target.value.replace(',', '.')) || 0;
                      setRate({ ...rate, [x.code]: v });
                      if (v > 0) setPick({ ...pick, [x.code]: true });
                    }}
                  />
                  <span className={css.muted}>
                    {x.pays_whole_hour
                      ? T('% tillegg')
                      : x.calculation_type === 'percent'
                        ? T('% av timelønn')
                        : T('kr per time')}
                  </span>
                </div>
                {x.pays_whole_hour && (
                  <div className={css.ruleNote}>
                    {T(
                      'Betaler grunnlønn og tillegg sammen ({p} % i PowerOffice). Timene flyttes fra Vanlige timer til denne linjen, så grunnlønnen betales én gang.',
                      { p: x.percent ?? '' },
                    )}
                  </div>
                )}
              </div>
            ))}
            {!regular?.suggestion && !list.length && (
              <p className={css.muted}>
                {T('Alle lønnsartene for tillegg og overtid finnes allerede i Timefly.')}
              </p>
            )}
          </div>
        )}
        <p className={css.muted}>
          {T(
            'Når et tillegg gjelder (kveld, helg, natt) vet ikke {system}. Det setter du under Innstillinger, Lønn.',
            { system: sys?.short ?? '' },
          )}
        </p>
        {zero.length > 0 && (
          <p className={css.error}>
            {T('Gi {names} en sats over 0 kr, ellers sendes timene uten tillegg.', {
              names: zero.map((x) => x.name).join(', '),
            })}
          </p>
        )}
        <div className={css.foot}>
          <Button
            tone="pri"
            disabled={busy || save.isPending || zero.length > 0}
            onClick={() => (n ? save.mutate() : void leftOut.save(PAY_KEY, offKeys).then(back))}
          >
            {save.isPending
              ? T('Lagrer…')
              : n === 1
                ? T('Opprett 1 regel')
                : n
                  ? T('Opprett {n} regler', { n })
                  : offKeys.length
                    ? T('Lagre valget')
                    : T('Tilbake til oversikten')}
          </Button>
          {n > 0 && (
            <button type="button" className={css.ghost} disabled={busy} onClick={back}>
              {T('Gjør dette senere')}
            </button>
          )}
        </div>
      </div>
    </Frame>
  );
}

/* ── stage 3: Hugin beside the setup ─────────────────────────────────────── */

interface Fact {
  key: string;
  label: string;
  saved: boolean;
  optional?: boolean;
  line: string;
  /** the ordinary form, always there beside Hugin (HUG-08) */
  to: string;
  /** what Hugin is asked, typed into its box and sent only by the person */
  ask?: string;
  /** what changed, to say «er lagret» once the server holds a new value */
  sig: string;
}
const hasHours = (o: unknown) =>
  !!o && typeof o === 'object' && Object.values(o as Record<string, unknown>).some((v) => v != null);
const hasNeed = (n: Record<string, Record<string, number>> | undefined) =>
  !!n && Object.values(n).some((d) => Object.values(d ?? {}).some((x) => x > 0));

/** What the company holds now, read back from the server, and what is still missing (OP-06, HUG-05). */
function useFacts(): { facts: Fact[]; loaded: boolean; people: number; payCount: number } {
  const { tenant, mode } = useTenant();
  const users = useUsers();
  const pay = usePayrollTypes();
  const types = useShiftTypes();
  const labour = useLabour();
  const onb = useOnboarding();
  const rota = mode !== 'timer';
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;
  const payCount = (Array.isArray(pay.data) ? pay.data.length : 0) + (tenant?.regular_pay_code ? 1 : 0);
  const shiftTypes = Array.isArray(types.data) ? types.data : [];
  const need = labour.query.data?.staffing_need;
  const missingHours = onb.data?.facts?.missing_hours ?? 0;
  const confirmed = tenant?.rule_profile?.status === 'confirmed';
  const facts: Fact[] = [
    {
      key: 'ansatte',
      label: T('Ansatte'),
      saved: people > 0,
      line: people ? T('{n} lagt til', { n: people }) : T('Ingen ennå'),
      to: '/ansatte',
      sig: String(people),
    },
    {
      key: 'lonnsarter',
      label: T('Lønnsarter'),
      saved: payCount > 0,
      optional: true,
      line: payCount ? T('{n} lagret', { n: payCount }) : T('Kan vente til første lønn'),
      to: '/innstillinger/lonn',
      sig: String(payCount),
    },
    {
      key: 'apningstider',
      label: T('Åpningstider'),
      saved: hasHours(tenant?.opening_hours),
      line: hasHours(tenant?.opening_hours) ? T('Lagret') : T('Når holder dere åpent?'),
      to: '/innstillinger/arbeidstid',
      ask: 'Sett åpningstidene våre: ',
      sig: JSON.stringify(tenant?.opening_hours ?? null),
    },
    ...(rota
      ? [
          {
            key: 'vakttyper',
            label: T('Vakttyper'),
            saved: shiftTypes.length > 0,
            line: shiftTypes.length
              ? shiftTypes
                  .slice(0, 3)
                  .map((s) =>
                    s.start_time && s.end_time
                      ? `${s.name} ${typeTime(s.start_time)}–${typeTime(s.end_time)}`
                      : s.name,
                  )
                  .join(', ')
              : T('Hvilke vakter har dere?'),
            to: '/innstillinger/typer',
            ask: 'Lag vakttyper for oss. Vi har disse vaktene: ',
            sig: shiftTypes.map((s) => `${s.id}:${s.start_time}-${s.end_time}`).join(','),
          },
          {
            key: 'bemanning',
            label: T('Bemanning'),
            saved: hasNeed(need),
            optional: true,
            line: hasNeed(need) ? T('Lagret') : T('Valgfritt ved oppstart'),
            to: '/innstillinger/bemanning',
            ask: 'Sett bemanningsbehovet per ukedag: ',
            sig: JSON.stringify(need ?? null),
          },
        ]
      : []),
    {
      key: 'regler',
      label: T('Arbeidstid og regler'),
      saved: confirmed,
      line: confirmed ? T('Gjennomgått') : T('Bransje, avtale og pauser'),
      to: '/innstillinger/regelprofil',
      sig: String(confirmed),
    },
    {
      key: 'opplysninger',
      label: T('Ansattopplysninger'),
      saved: people > 0 && missingHours === 0,
      line: !people
        ? T('Legg til ansatte først')
        : missingHours
          ? T('{n} mangler avtalt arbeidstid', { n: missingHours })
          : T('Avtalt arbeidstid er satt for alle'),
      to: '/ansatte?oppgave=arbeidstid',
      sig: String(missingHours),
    },
  ];
  const loaded =
    users.isSuccess && pay.isSuccess && types.isSuccess && onb.isSuccess && (!rota || !labour.pending);
  return { facts, loaded, people, payCount };
}

/**
 * Fullfør med AI (OP-06/07, HUG-05..08): Hugin's one conversation docked on the left, the setup on the
 * right: what is saved, the next missing step, and Hugin's proposals, each with its own Utfør. Hugin's
 * tools and confirmation cards are its own (#287); nothing here sends a message or saves a proposal, and a
 * receipt is said only when the server holds the new value. On a narrow screen Samtale and Oppsett switch,
 * and both keep their state.
 */
function FullforAI() {
  const { tenant, mode } = useTenant();
  const { go, busy, kilde } = useSetup();
  const qc = useQueryClient();
  const { facts, loaded, people, payCount } = useFacts();
  const pending = useDock((x) => x.pending);
  const [pane, setPane] = useState<'samtale' | 'oppsett'>('samtale');
  const [tab, setTab] = useState<'ditt' | 'forslag'>('ditt');
  const [receipt, setReceipt] = useState<string | null>(null);
  const receiptRef = useRef<HTMLParagraphElement>(null);
  const company = tenant?.brreg_name ?? tenant?.name ?? '';
  useEffect(() => {
    useDock.getState().set({
      line: `${T('Oppsett')}, ${company}`,
      placeholder: T('F.eks. Åpent 10–22 alle dager. To på dag, tre på kveld.'),
      setup: true,
    });
    return () => useDock.getState().set({ target: null, cards: null, placeholder: '', setup: false });
  }, [company]);
  // «Se forslaget» in a turn: the proposals pane, on a phone too
  useEffect(() => {
    const show = () => {
      setPane('oppsett');
      setTab('forslag');
    };
    window.addEventListener(SHOW_PROPOSALS, show);
    return () => window.removeEventListener(SHOW_PROPOSALS, show);
  }, []);
  // after each turn or decision the company is read again, so the setup shows what the server holds
  useEffect(() => {
    const again = () => {
      for (const key of [
        ['tenant'],
        ['labour-settings'],
        ['shift-types'],
        ['payroll-types'],
        ['users'],
        ['onboarding'],
      ])
        qc.invalidateQueries({ queryKey: key });
    };
    window.addEventListener('timefly:ai-pending', again);
    return () => window.removeEventListener('timefly:ai-pending', again);
  }, [qc]);
  // a receipt for what the server now holds and did not before (OP-07): the value changed, not a click
  const sigs = facts.map((f) => `${f.key}=${f.sig}`).join('|');
  const before = useRef<Record<string, string> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the signatures are the trigger
  useEffect(() => {
    if (!loaded) return;
    const now = Object.fromEntries(facts.map((f) => [f.key, f.sig]));
    if (before.current) {
      const changed = facts.find((f) => f.saved && before.current?.[f.key] !== now[f.key]);
      if (changed) {
        setReceipt(T('{what} er lagret.', { what: changed.label }));
        setTab('ditt');
        requestAnimationFrame(() => receiptRef.current?.focus());
      }
    }
    before.current = now;
  }, [sigs, loaded]);
  const nextFact = facts.find((f) => !f.saved && !f.optional) ?? facts.find((f) => !f.saved);
  const savedLine = T('{a} ansatte og {b} lønnsarter er lagret.', { a: people, b: payCount });
  const ask = (f: Fact) => {
    if (!f.ask) return;
    draftForHugin(T(f.ask));
    setPane('samtale');
  };
  return (
    <Frame
      title={T('Fullfør oppsettet med AI')}
      sub={T('Hugin bruker det som er på plass og hjelper med resten. Ingenting lagres før du sier ja.')}
      aside={
        <Link className={css.textLink} to="/kom-i-gang">
          {T('Se alle steg')}
        </Link>
      }
    >
      <div className={css.switch} role="tablist" aria-label={T('Visning')}>
        {(
          [
            ['samtale', T('Samtale')],
            ['oppsett', T('Oppsett')],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={pane === k}
            aria-controls={`oppsett-pane-${k}`}
            onClick={() => setPane(k)}
          >
            {label}
            {k === 'oppsett' && pending > 0 ? ` (${pending})` : ''}
          </button>
        ))}
      </div>
      <div className={css.ai} data-pane={pane}>
        <div className={css.chatPane} id="oppsett-pane-samtale" data-testid="setup-pane-samtale">
          <div className={css.chatHead}>
            <b>
              <Icon name="hugin" size={18} /> {T('Hugin')}
            </b>
            <span>{T('Oppsettsassistent')}</span>
          </div>
          <div className={css.context} data-testid="setup-context">
            <Icon name="store" size={16} />
            <span>
              <b>{company}</b>
              <span>{[nameOf(kilde), savedLine].filter(Boolean).join('. ')}</span>
            </span>
            <button
              type="button"
              className={css.textLink}
              disabled={busy}
              onClick={() => go({ setup_step: 'company' })}
            >
              {T('Se import')}
            </button>
          </div>
          <div
            className={css.chat}
            ref={(el) => useDock.getState().set({ target: el })}
            data-testid="setup-hugin"
          />
          <PocChatPanel />
          <div className={css.asks}>
            {facts
              .filter((f) => f.ask && (mode !== 'timer' || f.key === 'apningstider'))
              .map((f) => (
                <button key={f.key} type="button" onClick={() => ask(f)}>
                  {f.key === 'apningstider'
                    ? T('Sett åpningstider')
                    : f.key === 'vakttyper'
                      ? T('Lag vakttyper')
                      : T('Legg til bemanning')}
                </button>
              ))}
          </div>
        </div>
        <div className={css.setupPane} id="oppsett-pane-oppsett" data-testid="setup-pane-oppsett">
          <div className={css.tabs} role="tablist" aria-label={T('Oppsett')}>
            <button type="button" role="tab" aria-selected={tab === 'ditt'} onClick={() => setTab('ditt')}>
              {T('Oppsettet ditt')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'forslag'}
              onClick={() => setTab('forslag')}
            >
              {pending ? T('Forslag ({n})', { n: pending }) : T('Forslag')}
            </button>
          </div>
          <div hidden={tab !== 'ditt'} className={css.setupBody}>
            {receipt && (
              <p className={css.receipt} ref={receiptRef} tabIndex={-1} data-testid="setup-receipt">
                <span className={css.okDot} aria-hidden="true">
                  <Icon name="check" size={14} strokeWidth={2.4} />
                </span>
                <span>
                  <b>{receipt}</b>
                  <span>{T('Det du har lagret, er beholdt. Neste steg står under.')}</span>
                </span>
              </p>
            )}
            {/* right after a save from a proposal: its guarded Undo (#154 HUG-06) */}
            <UndoList onUndone={() => setReceipt(null)} />
            {nextFact ? (
              <div className={css.nextStep} data-testid="setup-next-step">
                <h2>{T('Neste steg: {what}', { what: nextFact.label.toLowerCase() })}</h2>
                <p>{nextFact.line}</p>
                <div className={css.foot}>
                  {nextFact.ask ? (
                    <Button tone="pri" onClick={() => ask(nextFact)}>
                      {T('Be Hugin om et forslag')}
                    </Button>
                  ) : null}
                  <Link className={nextFact.ask ? css.textLink : 'btn pri'} to={nextFact.to}>
                    {T('Åpne skjemaet')}
                  </Link>
                </div>
              </div>
            ) : (
              <div className={css.nextStep} data-testid="setup-next-step">
                <h2>{T('Det viktigste er på plass')}</h2>
                <p>{T('Se over prøveperioden og fullfør oppsettet.')}</p>
              </div>
            )}
            <ul className={css.factList} data-testid="setup-facts">
              {facts.map((f) => (
                <li key={f.key} data-saved={f.saved || undefined}>
                  <span className={f.saved ? css.okDot : css.openDot} aria-hidden="true">
                    {f.saved ? (
                      <Icon name="check" size={14} strokeWidth={2.4} />
                    ) : (
                      <Icon name="chevronRight" size={14} />
                    )}
                  </span>
                  <Link to={f.to}>
                    <b>{f.label}</b>
                    <span>{f.line}</span>
                  </Link>
                  <span className="sr-only">
                    {f.saved ? T('Lagret') : f.optional ? T('Valgfritt') : T('Mangler')}
                  </span>
                </li>
              ))}
            </ul>
            {mode !== 'timer' && people > 0 && (
              <p className={css.muted}>
                {T('Du kan lage et planutkast nå. Lønnsoppsettet kan fullføres før første overføring.')}{' '}
                <Link className={css.textLink} to="/vaktplan">
                  {T('Lag første vaktplan')}
                </Link>
              </p>
            )}
          </div>
          <div hidden={tab !== 'forslag'} className={css.setupBody}>
            {pending === 0 && (
              <p className={css.muted} data-testid="setup-no-proposals">
                {T(
                  'Ingen forslag venter. Skriv til Hugin, så kommer forslagene hit, hvert med sin egen lagring.',
                )}
              </p>
            )}
            <div ref={(el) => useDock.getState().set({ cards: el })} data-testid="setup-proposals" />
          </div>
          <div className={css.setupFoot}>
            <Button tone="pri" disabled={busy} onClick={() => go({ setup_step: 'review' })}>
              {T('Fullfør oppsettet')}
            </Button>
            <Link className={css.textLink} to="/kom-i-gang">
              {T('Alle steg og påminnelser')}
            </Link>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/** The trial and, with billing on, the price and the card (plan 0028 slice 4). */
function Review() {
  const { tenant } = useTenant();
  const { go, busy } = useSetup();
  const nav = useNavigate();
  const users = useUsers();
  const pay = usePayrollTypes();
  const types = useShiftTypes();
  // the regular hours' code set from the payroll system is a pay type too (#191 F4)
  const payCount = (Array.isArray(pay.data) ? pay.data.length : 0) + (tenant?.regular_pay_code ? 1 : 0);
  const billingOn = !!tenant?.billing?.enabled && !tenant.is_demo;
  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: () => get<Billing>('/billing'),
    enabled: billingOn,
  });
  const [paying, setPaying] = useState(false);
  if (!tenant) return null;
  const people = (users.data ?? []).filter((u) => u.active_status !== false).length;
  const q = billing.data?.quote;
  // finish, then Stripe's Checkout; it comes back to Abonnement, which waits for Stripe's word
  async function finishAndPay() {
    if (!(await go({ setup_step: null }))) return;
    setPaying(true);
    try {
      window.location.assign((await post<{ url: string }>('/billing/checkout', {})).url);
    } catch (e) {
      toast(errorText(e));
      nav('/abonnement', { replace: true });
    }
  }
  return (
    <Frame
      title={
        tenant.workspace?.enabled?.scheduling === false
          ? T('{company} er klar til å registrere timer.', { company: tenant.brreg_name ?? tenant.name })
          : T('{company} er klar for første plan.', { company: tenant.brreg_name ?? tenant.name })
      }
      aside={
        <button
          type="button"
          className={css.textLink}
          disabled={busy}
          onClick={() => go({ setup_step: 'hugin' })}
        >
          {T('Tilbake til Hugin')}
        </button>
      }
    >
      <div className={css.panel}>
        <div className={css.stats}>
          <div>
            <b>{people}</b>
            <span>{T('ansatte')}</span>
          </div>
          <div>
            <b>{payCount}</b>
            <span>{T('lønnsarter')}</span>
          </div>
          {tenant.workspace?.enabled?.scheduling !== false && (
            <div>
              <b>{Array.isArray(types.data) ? types.data.length : 0}</b>
              <span>{T('vakttyper')}</span>
            </div>
          )}
        </div>
        <ul className={css.summary}>
          {tenant.trial_ends_at && (
            <li>
              {T('Gratis prøveperiode til {date}. Du blir ikke belastet.', {
                date: fmtD(osloDay(tenant.trial_ends_at)),
              })}
            </li>
          )}
          {q && (
            <li data-testid="setup-price">
              {T(
                'Deretter {sum} per måned eks. mva ({base} inkluderer {included} ansatte, du har {seats}; administratorer teller ikke).',
                {
                  sum: kr(q.monthly_ex_vat),
                  seats: q.seats,
                  base: kr(q.base_kr),
                  included: q.included,
                },
              )}
            </li>
          )}
          <li>{T('De ansatte får ingen e-post før du sender velkomsten fra Ansatte.')}</li>
          <li>
            {T(
              'Det som ikke er gjort, står igjen under Kom i gang. Ingenting regnes som ferdig før det er lagret.',
            )}
          </li>
        </ul>
        <div className={css.foot}>
          {billingOn ? (
            <>
              <Button tone="pri" disabled={busy || paying} onClick={finishAndPay}>
                {T('Legg inn kort og start')}
              </Button>
              <button
                type="button"
                className={css.ghost}
                disabled={busy || paying}
                onClick={() => go({ setup_step: null }, true)}
              >
                {T('Ferdig, legg inn kort senere')}
              </button>
            </>
          ) : (
            <Button tone="pri" disabled={busy} onClick={() => go({ setup_step: null }, true)}>
              {T('Ferdig')}
            </Button>
          )}
        </div>
        {billingOn && (
          <p className={css.muted}>
            {T('Kortet belastes først når prøveperioden er over. Du kan si opp når som helst.')}
          </p>
        )}
        <p className={css.links}>
          <a href={HELP} target="_blank" rel="noreferrer">
            {T('Få hjelp med oppsettet')}
          </a>
        </p>
      </div>
    </Frame>
  );
}
